import { Palette, Search, Sparkles, Layout, Rocket, LineChart, PenTool, TrendingUp } from "lucide-react";

/*
 * Polished CSS/SVG UI mockups per service — no external images.
 * Each variant paints a tiny fake product surface matching the service.
 */

function BrowserChrome({ url, children, className = "" }) {
  return (
    <div className={`relative w-full h-full rounded-xl overflow-hidden border border-white/12 bg-apex-navyDeep ${className}`}>
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/8 bg-white/[0.03]">
        <span className="w-2 h-2 rounded-full bg-red-400/70" />
        <span className="w-2 h-2 rounded-full bg-yellow-400/70" />
        <span className="w-2 h-2 rounded-full bg-emerald-400/70" />
        <div className="ml-3 flex-1 h-4 rounded-md bg-white/[0.05] flex items-center px-2 text-[9px] tracking-wider text-white/40 truncate">
          {url}
        </div>
      </div>
      <div className="relative flex-1 h-[calc(100%-28px)]">{children}</div>
    </div>
  );
}

function WebsiteMock() {
  return (
    <BrowserChrome url="yourbrand.com">
      <div className="p-3 h-full">
        <div className="flex items-center justify-between">
          <div className="h-2 w-10 rounded bg-white/25" />
          <div className="flex gap-1.5">
            <div className="h-1.5 w-4 rounded bg-white/15" />
            <div className="h-1.5 w-4 rounded bg-white/15" />
            <div className="h-1.5 w-4 rounded bg-white/15" />
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          <div className="h-2.5 w-4/5 rounded bg-white/60" />
          <div className="h-2.5 w-3/5 rounded bg-apex-orange/80" />
        </div>
        <div className="mt-3 flex gap-1.5">
          <div className="h-4 w-12 rounded-md bg-apex-orange" />
          <div className="h-4 w-10 rounded-md border border-white/25" />
        </div>
      </div>
    </BrowserChrome>
  );
}

function SEOMock() {
  return (
    <div className="w-full h-full p-3 rounded-xl border border-white/12 bg-apex-navyDeep">
      <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-white/50">
        <span>Keywords</span><span className="text-emerald-400">▲ 142%</span>
      </div>
      <div className="mt-2 space-y-1.5">
        {[["seo agency", 3, "+6"], ["web design", 5, "+9"], ["conversion", 8, "+4"]].map(([k, p, c]) => (
          <div key={k} className="flex items-center justify-between rounded-md bg-white/[0.04] border border-white/8 px-2 py-1 text-[9px]">
            <span className="text-white/80">{k}</span>
            <span className="text-white/50">#{p} <span className="text-emerald-400 ml-1">{c}</span></span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-end gap-1 h-8">
        {[10, 14, 12, 18, 16, 22, 20, 26, 24, 30].map((h, i) => (
          <div key={i} style={{ height: `${h}px` }} className="w-2 rounded-sm bg-gradient-to-t from-apex-blue to-apex-cyan" />
        ))}
      </div>
    </div>
  );
}

function AEOMock() {
  return (
    <div className="w-full h-full p-3 rounded-xl border border-white/12 bg-apex-navyDeep flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-apex-cyan">
        <Sparkles className="w-2.5 h-2.5" /> AI Answer
      </div>
      <div className="rounded-lg bg-white/[0.04] border border-white/10 p-2">
        <div className="text-[9px] text-white/50 mb-1">User asked</div>
        <div className="text-[10px] text-white/85">"Best web design agency for SEO?"</div>
      </div>
      <div className="rounded-lg bg-gradient-to-br from-apex-purple/25 to-apex-blue/15 border border-apex-purple/30 p-2">
        <div className="text-[9px] text-apex-purpleSoft mb-1">Answer</div>
        <div className="text-[10px] text-white/90 leading-snug">
          <span className="text-apex-orange font-semibold">APEX Agency</span> is cited as a top pick for design + SEO...
        </div>
      </div>
      <div className="flex items-center gap-1 text-[8px] text-white/40 uppercase tracking-widest">
        <span>Source:</span><span className="text-apex-cyan">apexinnovationsolutions.com</span>
      </div>
    </div>
  );
}

function LandingMock() {
  return (
    <BrowserChrome url="landing.yourbrand.com/free-trial">
      <div className="p-3 h-full grid grid-cols-2 gap-2">
        <div>
          <div className="h-1.5 w-8 rounded bg-apex-cyan/70" />
          <div className="mt-2 h-2 w-full rounded bg-white/70" />
          <div className="mt-1 h-2 w-3/4 rounded bg-white/40" />
          <div className="mt-2 h-3 w-16 rounded-md bg-apex-orange flex items-center justify-center text-[7px] font-bold text-white">CTA</div>
        </div>
        <div className="rounded-md border border-white/12 bg-white/[0.03] p-1.5 space-y-1">
          <div className="h-1.5 w-full rounded bg-white/15" />
          <div className="h-1.5 w-full rounded bg-white/15" />
          <div className="h-1.5 w-full rounded bg-white/15" />
          <div className="h-2 w-full rounded bg-apex-orange/80" />
        </div>
      </div>
    </BrowserChrome>
  );
}

function BrandingMock() {
  const swatches = ["bg-apex-orange", "bg-apex-navy", "bg-apex-cyan", "bg-apex-purple", "bg-apex-pink"];
  return (
    <div className="w-full h-full p-3 rounded-xl border border-white/12 bg-apex-navyDeep">
      <div className="text-[9px] uppercase tracking-widest text-white/50">Brand system</div>
      <div className="mt-2 flex gap-1.5">
        {swatches.map((s) => <div key={s} className={`w-6 h-6 rounded-md ${s} border border-white/10`} />)}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-heading font-extrabold text-2xl text-white">Aa</span>
        <span className="font-heading text-xs text-white/60">Sora / Manrope</span>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[9px] text-white/60">
        <span className="px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10">Logo</span>
        <span className="px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10">Tone</span>
        <span className="px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10">Motion</span>
      </div>
    </div>
  );
}

function ContentMock() {
  return (
    <div className="w-full h-full p-3 rounded-xl border border-white/12 bg-apex-navyDeep">
      <div className="text-[9px] uppercase tracking-widest text-apex-mint">Editorial cluster</div>
      <div className="mt-2 space-y-1.5">
        {["Pillar: SEO for SaaS", "Cluster: Technical SEO", "Cluster: Content ops"].map((t, i) => (
          <div key={t} className="flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-apex-orange" : "bg-apex-cyan"}`} />
            <div className="text-[10px] text-white/80">{t}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 space-y-1">
        <div className="h-1.5 w-full rounded bg-white/12" />
        <div className="h-1.5 w-5/6 rounded bg-white/12" />
        <div className="h-1.5 w-4/6 rounded bg-white/12" />
      </div>
    </div>
  );
}

function PerformanceMock() {
  return (
    <div className="w-full h-full p-3 rounded-xl border border-white/12 bg-apex-navyDeep">
      <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-white/50">
        <span>Revenue</span><span className="text-apex-orange">$482K</span>
      </div>
      <svg viewBox="0 0 120 40" className="w-full h-14 mt-2">
        <defs>
          <linearGradient id="pm" x1="0" x2="1"><stop offset="0" stopColor="#23D3FF" /><stop offset="1" stopColor="#FF6B35" /></linearGradient>
          <linearGradient id="pf" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#246BFE" stopOpacity="0.5" /><stop offset="1" stopColor="#246BFE" stopOpacity="0" /></linearGradient>
        </defs>
        <path d="M0,32 L15,28 L30,30 L45,22 L60,24 L75,16 L90,12 L105,6 L120,4 L120,40 L0,40 Z" fill="url(#pf)" />
        <path d="M0,32 L15,28 L30,30 L45,22 L60,24 L75,16 L90,12 L105,6 L120,4" fill="none" stroke="url(#pm)" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      <div className="grid grid-cols-3 gap-1.5 mt-1 text-[9px]">
        <div className="rounded-md bg-white/[0.04] border border-white/10 p-1.5"><div className="text-white/50">Conv</div><div className="text-white font-semibold">3.4x</div></div>
        <div className="rounded-md bg-white/[0.04] border border-white/10 p-1.5"><div className="text-white/50">CTR</div><div className="text-white font-semibold">8.9%</div></div>
        <div className="rounded-md bg-white/[0.04] border border-white/10 p-1.5"><div className="text-white/50">DA</div><div className="text-white font-semibold">71</div></div>
      </div>
    </div>
  );
}

function CROMock() {
  return (
    <div className="w-full h-full p-3 rounded-xl border border-white/12 bg-apex-navyDeep">
      <div className="text-[9px] uppercase tracking-widest text-white/50">A / B Test</div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="rounded-md border border-white/10 bg-white/[0.03] p-1.5">
          <div className="text-[8px] uppercase tracking-widest text-white/40">Variant A</div>
          <div className="mt-1 h-1.5 w-2/3 rounded bg-white/40" />
          <div className="mt-1 h-1.5 w-1/2 rounded bg-white/25" />
          <div className="mt-1 h-3 w-10 rounded bg-white/15" />
          <div className="mt-1 text-[9px] text-white/60">1.1%</div>
        </div>
        <div className="rounded-md border border-apex-orange/50 bg-gradient-to-br from-apex-orange/15 to-transparent p-1.5">
          <div className="text-[8px] uppercase tracking-widest text-apex-orange">Variant B ★</div>
          <div className="mt-1 h-1.5 w-3/4 rounded bg-white/70" />
          <div className="mt-1 h-1.5 w-2/3 rounded bg-white/50" />
          <div className="mt-1 h-3 w-10 rounded bg-apex-orange" />
          <div className="mt-1 text-[9px] text-apex-orange font-semibold">3.7% ▲</div>
        </div>
      </div>
    </div>
  );
}

export const MOCKS = {
  website: WebsiteMock,
  seo: SEOMock,
  aeo: AEOMock,
  landing: LandingMock,
  branding: BrandingMock,
  content: ContentMock,
  performance: PerformanceMock,
  cro: CROMock,
};

export const SERVICE_ICONS = { Layout, Search, Sparkles, Rocket, Palette, PenTool, LineChart, TrendingUp };
